import { Controller } from "stimulus";
import Turbolinks from "turbolinks";
import { getMetaValue } from "../utils/helpers";
import { renderInModal } from "../utils/modals";

export default class extends Controller {
  static targets = ["form", "container", "label", "radioButton", "givingAmount", "productSelect", "splitCausesConfig", "splitCausesError"]

  connect() {
    this.element.addEventListener("submit", this._validateOnSubmit.bind(this));
  }

  _validateOnSubmit(e) {
    if (!this._isSplitCausesActive()) return;
    const error = this._validateSplitPercentages();
    if (error) {
      e.preventDefault();
      e.stopPropagation();
      this._showSplitError(error);
    } else {
      this._hideSplitError();
    }
  }

  _isSplitCausesActive() {
    if (!this.hasSplitCausesConfigTarget) return false;
    const radio = this.element.querySelector('input[name*="collection_cause_type"][value="split_causes"]:checked') ||
                  this.element.querySelector('input[name*="select_cause_type"][value="split_causes"]:checked');
    return !!radio;
  }

  _validateSplitPercentages() {
    const inputs = this._splitPercentageInputs();
    if (inputs.length === 0) return null;

    for (const input of inputs) {
      const val = parseFloat(input.value);
      if (!input.value || isNaN(val) || val <= 0) {
        return "All causes must have a percentage greater than 0.";
      }
    }

    const total = Array.from(inputs).reduce((sum, input) => sum + (parseFloat(input.value) || 0), 0);
    if (Math.abs(total - 100) > 0.01) {
      return `Split percentages must add up to 100% (currently ${total.toFixed(2)}%).`;
    }

    return null;
  }

  _splitPercentageInputs() {
    if (!this.hasSplitCausesConfigTarget) return [];
    return this.splitCausesConfigTarget.querySelectorAll('input[type="number"][name*="split_percentage"]');
  }

  _showSplitError(message) {
    if (this.hasSplitCausesErrorTarget) {
      this.splitCausesErrorTarget.textContent = message;
      this.splitCausesErrorTarget.style.display = "";
    } else {
      const errorDiv = document.createElement("div");
      errorDiv.className = "alert alert-danger mt-2";
      errorDiv.setAttribute("data-donations-target", "splitCausesError");
      errorDiv.textContent = message;
      this.splitCausesConfigTarget.appendChild(errorDiv);
    }
  }

  _hideSplitError() {
    if (this.hasSplitCausesErrorTarget) {
      this.splitCausesErrorTarget.style.display = "none";
    }
  }

  change_label(e) {
    if(e.currentTarget.value === "percentage"){
      this.labelTarget.innerText = "%";
      this.givingAmountTarget.setAttribute('max', '100');
    } else {
      this.labelTarget.innerText = this.labelTarget.dataset["currency"];
      this.givingAmountTarget.removeAttribute('max');
    }
  }

  submitForm(e) {
    this.formTarget.submit();
  }

  toggleSplitCauses(e) {
    if (this.hasSplitCausesConfigTarget) {
      const isSplit = e.currentTarget.value === "split_causes";
      this.splitCausesConfigTarget.style.display = isSplit ? "" : "none";
      if (isSplit) {
        this._autoFillSplitPercentages(this.splitCausesConfigTarget);
      } else {
        this._hideSplitError();
      }
    }
  }

  validateSplitInput() {
    const error = this._validateSplitPercentages();
    if (error) {
      this._showSplitError(error);
    } else {
      this._hideSplitError();
    }
  }

  _autoFillSplitPercentages(container) {
    const inputs = container.querySelectorAll('input[type="number"][name*="split_percentage"]');
    const allBlank = Array.from(inputs).every(input => !input.value || parseFloat(input.value) === 0);
    if (allBlank && inputs.length > 0) {
      const equal = Math.floor((100 / inputs.length) * 100) / 100;
      const remainder = Math.round((100 - equal * inputs.length) * 100) / 100;
      inputs.forEach((input, i) => {
        input.value = i === inputs.length - 1 ? equal + remainder : equal;
      });
    }
  }

  async submitFormAsync() {
    const data = new FormData(this.formTarget);
    const response = await fetch(this.formTarget.action, {
      method: "PUT",
      body: data,
      headers: {
        'X-Requested-With':'XMLHttpRequest',
        "X-CSRF-Token": getMetaValue("csrf-token"),
      }
    })
    const redirect = response.headers.get('Location');
    if (redirect) {
      Turbolinks.visit(redirect);
    } else {
      renderInModal(await response.text())
    }
  }
}
